import AppContext from 'App/AppContext';
import { IPIconButton, IPPrimaryButton, IPSpinner } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import HrefLink from 'components/Links/HrefLink';
import WarningMessage from 'components/Notification/WarningMessage';
import AddNewListPanel from 'components/lists/AddNewListPanel';
import { getCategoryMenuProps, isEligibleCategory } from 'components/OutputLocations/CategoryMenu';
import { bindCategory, createCategory } from 'components/OutputLocations/outputLocationActions';
import { editIcon, globalStackTokensGapExtraSmall, globalStackTokensGapSmall } from 'globalStyles';
import { TFunction } from 'i18next';
import { DocumentProviderType } from 'models/documentProvider';
import DocumentOutputLocation, { DocumentOutputPurpose } from 'models/documentOutputLocation';
import ResourceList from 'models/resourceList';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetOutputLocations } from 'services/Api/documentOutputLocationService';
import { apiGetLinksForList } from 'services/Api/linkService';
import { apiRequest } from 'services/Auth/authConfig';
import { getGraphSharepointErrorMsg, isUploadCancelledError, toAppError } from 'services/Graph/graphErrors';
import AppError from 'utils/appError';
import { uploadDefaultReportTemplate } from './reportStorageSetup';

interface IReportOutputLocationSetupProps {
  //visibility of the hosting modal. used to (re)load the current location when it opens
  isOpen: boolean;
  disabled?: boolean;
  //after a category was bound and its template placed: the copilot card re-checks its setup and resumes the process
  onChanged?: () => void;
}

//
// The general reports row inside the Default integration modal: the output location and a button with the category
// menu. When a SharePoint category without a template is bound, the default Word template is placed; an existing
// template, which may have been customized, is never replaced here.
//
const ReportOutputLocationSetup = (props: IReportOutputLocationSetupProps) => {
  const { t } = useTranslation(['adminReports', 'library', 'outputLocations', 'exceptions']);
  const appContext = useContext(AppContext);
  const [location, setLocation] = useState<DocumentOutputLocation | undefined>(undefined);
  const [createCategoryOpen, setCreateCategoryOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  useEffect(() => {
    if (props.isOpen) {
      loadData(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen]);

  const getGeneralReports = async (): Promise<DocumentOutputLocation | undefined> => {
    const accessToken = await appContext.getAccessToken(apiRequest.scopes);
    const locations = await apiGetOutputLocations(accessToken);

    return locations.find((item) => item.purpose === DocumentOutputPurpose.GeneralReports);
  };

  const loadData = async (showSpinner: boolean) => {
    try {
      if (showSpinner) setIsLoading(true);
      //the category menu lists the categories from the cache, so they are loaded before the menu can open
      await appContext.globalDataCache.lists.getItems();
      setLocation(await getGeneralReports());
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const bindAndPlaceTemplate = async (list: ResourceList) => {
    if (!location) return;
    try {
      setIsUpdating(true);
      await bindCategory(appContext, location, list);
      const current = await getGeneralReports();
      setLocation(current);

      const isSharePoint =
        (current?.providerType ?? DocumentProviderType.SharePoint) === DocumentProviderType.SharePoint;
      if (current?.listId === undefined || current.templateLinkId || !isSharePoint) {
        props.onChanged?.();

        return;
      }

      await appContext.globalDataCache.lists.getItems();
      const boundList = appContext.globalDataCache.lists.get(current.listId);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const listLinks = await apiGetLinksForList(boundList.listId, accessToken, appContext.globalDataCache);
      await uploadDefaultReportTemplate(appContext, boundList, listLinks, current.templateLinkId);
      setLocation(await getGeneralReports());
      props.onChanged?.();
    } catch (err) {
      //a cancel is a user action and needs no message
      if (isUploadCancelledError(err)) {
        return;
      }
      //shown bottom-center: this block sits in the Default integration modal
      const graphErrorMsg = getGraphSharepointErrorMsg(err, t);
      if (AppError.isControllerException(err)) {
        appContext.showNotification(AppError.getControllerExceptionMsg(err, t), true, false, true);
      } else if (graphErrorMsg) {
        appContext.showNotification(t('library:Picker.FileErrorUpload', { info: graphErrorMsg }), true, false, true);
      } else {
        appContext.setError(toAppError(err));
      }
    } finally {
      setIsUpdating(false);
    }
  };

  //the new category is bound at once when the general reports can use it
  const onCategoryCreated = async (created: ResourceList) => {
    if (!location) return;
    try {
      const saved = await createCategory(appContext, created);
      if (isEligibleCategory(location, saved)) {
        await bindAndPlaceTemplate(saved);
      } else {
        appContext.showNotification(t('exceptions:OutputLocation.NotADocumentLibrary'), true, false, true);
      }
    } catch (err) {
      appContext.setError(err);
    }
  };

  const categoryMenuProps = location
    ? getCategoryMenuProps(appContext, t as TFunction<string[]>, {
        location,
        selectedListId: location.listId,
        onPick: (list) => bindAndPlaceTemplate(list),
        onCreateNew: () => setCreateCategoryOpen(true),
      })
    : undefined;

  //
  // Main render
  //
  if (isLoading) {
    //render the spinner until the current location is known. rendering the block right away would
    //flash the warning for a moment when a location has in fact been configured.
    return <IPSpinner size={IPSpinnerSize.small} />;
  }

  const isSet = location?.listId !== undefined;

  return (
    <Fragment>
      <IPStack tokens={globalStackTokensGapSmall}>
        {!isSet && (
          <IPStack.Item>
            <WarningMessage message={t('adminReports:OutputLocationSetup.Warning')} />
          </IPStack.Item>
        )}
        <IPStack.Item>
          <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapExtraSmall}>
            {isSet && (
              <IPStack.Item>
                <HrefLink href={location?.listUrl} external>
                  {location?.listName}
                </HrefLink>
              </IPStack.Item>
            )}
            {isSet && (
              <IPStack.Item>
                <IPIconButton
                  iconProps={editIcon}
                  disabled={props.disabled || isUpdating}
                  menuProps={categoryMenuProps}
                  onRenderMenuIcon={() => null}
                />
              </IPStack.Item>
            )}
            {!isSet && (
              <IPStack.Item>
                <IPPrimaryButton disabled={props.disabled || isUpdating || !location} menuProps={categoryMenuProps}>
                  {t('adminReports:OutputLocationSetup.Button')}
                </IPPrimaryButton>
              </IPStack.Item>
            )}
            {isUpdating && (
              <IPStack.Item>
                <IPSpinner size={IPSpinnerSize.small} />
              </IPStack.Item>
            )}
          </IPStack>
        </IPStack.Item>
      </IPStack>
      {createCategoryOpen && (
        <AddNewListPanel
          isOpen={createCategoryOpen}
          renderAsPanel={false}
          onDismiss={() => setCreateCategoryOpen(false)}
          onAddNew={(created: ResourceList) => onCategoryCreated(created)}
        />
      )}
    </Fragment>
  );
};

export default ReportOutputLocationSetup;
